import React, { useEffect, useState } from "react";
import {
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Text,
    Spinner,
    Flex,
    Button,
    Tag,
    Center,
    useColorModeValue,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Divider,
    Grid,
    GridItem,
} from "@chakra-ui/react";
import Pagination from "react-js-pagination"; // ✅ pagination import
import { fetchWrapper } from "../../helpers/fetch-wrapper";
import moment from "moment";
import Header from "../../components/Header";
import BoxWrapper from "../../components/BoxWrapper/BoxWrapper";

const EstimatesList = () => {
    const [list, setList] = useState([]);
    const [pagination, setPagination] = useState({});
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [selected, setSelected] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // ✅ Load estimate list with pagination
    const loadList = async (pageNum = 1) => {
        try {
            setLoading(true);
            const url = `https://web-api.xnotify.com/estimate/admin/list?page=${pageNum}&limit=10`;
            const data = await fetchWrapper.get(url);

            if (data?.success) {
                setList(data.data.estimates || []);
                setPagination(data.data.pagination || {});
            }
        } catch (error) {
            console.error("Error fetching estimates:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadList(page);
    }, [page]);

    const getStatusColor = (status) => {
        switch (status) {
            case "draft":
                return "yellow";
            case "finalized":
                return "green";
            case "archived":
                return "gray";
            default:
                return "gray";
        }
    };

    const openModal = (estimate) => {
        setSelected(estimate);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setSelected(null);
        setIsModalOpen(false);
    };

    return (
        <>
            <Header />
            <BoxWrapper>
                {/* Header */}
                <Flex justify="space-between" align="center" mb={4}>
                    <Text fontSize="xl" fontWeight="bold" color="gray.700">
                        Estimates
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                        Total: {pagination?.total || 0}
                    </Text>
                </Flex>

                {/* Table */}
                {loading ? (
                    <Center py={20}>
                        <Spinner size="xl" />
                    </Center>
                ) : (
                    <Table variant="simple" colorScheme="gray" size="md">
                        <Thead>
                            <Tr>
                                <Th>ID</Th>
                                <Th>Estimate Name</Th>
                                <Th>Total Volume</Th>
                                <Th>Currency</Th>
                                <Th>Right Route Cost</Th>
                                <Th>Created By</Th>
                                <Th>Created At</Th>
                                <Th textAlign="center">Action</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {list.length > 0 ? (
                                list.map((es, index) => (
                                    <Tr
                                        key={es.id}
                                        _hover={{ bg: "gray.50" }}
                                        bg={index % 2 === 0 ? "white" : "gray.50"}
                                    >
                                        <Td>{es.id}</Td>
                                        <Td fontWeight="600" color="blue.600">
                                            {es.estimate_name}
                                        </Td>
                                        <Td>{es.total_message_volume.toLocaleString()}</Td>
                                        <Td>{es.billing_currency}</Td>
                                        <Td>{es.right_route_cost}</Td>
                                        <Td>
                                            {es.user
                                                ? `${es.user.first_name} ${es.user.last_name}`
                                                : "-"}
                                        </Td>
                                        <Td>{moment(es.created_at).format("DD MMM YYYY")}</Td>
                                        <Td textAlign="center">
                                            <Button
                                                size="sm"
                                                colorScheme="blue"
                                                variant="outline"
                                                onClick={() => openModal(es)}
                                            >
                                                View
                                            </Button>
                                        </Td>
                                    </Tr>
                                ))
                            ) : (
                                <Tr>
                                    <Td colSpan={9} textAlign="center" py={10}>
                                        No estimates found.
                                    </Td>
                                </Tr>
                            )}
                        </Tbody>
                    </Table>
                )}

                {/* ✅ Pagination with react-js-pagination */}
                {!loading && pagination?.pages > 1 && (
                    <Flex justify="center" mt={8}>
                        <Pagination
                            activePage={page}
                            itemsCountPerPage={pagination.limit || 10}
                            totalItemsCount={pagination.total || 0}
                            pageRangeDisplayed={3}
                            onChange={(pageNum) => {
                                setPage(pageNum);
                                window.scrollTo({ top: 0, behavior: "smooth" });
                            }}
                            itemClass="page-item"
                            linkClass="page-link"
                            innerClass="pagination"
                        />
                    </Flex>
                )}
            </BoxWrapper>

            {/* ✅ Details Modal */}
            <Modal isOpen={isModalOpen} onClose={closeModal} size="xl" scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Estimate Details</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {selected ? (
                            <Box>
                                <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                                    <GridItem>
                                        <Text fontWeight="600">Estimate Name:</Text>
                                        <Text>{selected.estimate_name}</Text>
                                    </GridItem>
                                    <GridItem>
                                        <Text fontWeight="600">Status:</Text>
                                        <Tag colorScheme={getStatusColor(selected.status)}>
                                            {selected.status}
                                        </Tag>
                                    </GridItem>

                                    <GridItem>
                                        <Text fontWeight="600">Currency:</Text>
                                        <Text>{selected.billing_currency}</Text>
                                    </GridItem>
                                    <GridItem>
                                        <Text fontWeight="600">Total Volume:</Text>
                                        <Text>{selected.total_message_volume.toLocaleString()}</Text>
                                    </GridItem>

                                    <GridItem>
                                        <Text fontWeight="600">Right Route Cost:</Text>
                                        <Text>{selected.right_route_cost}</Text>
                                    </GridItem>
                                    <GridItem>
                                        <Text fontWeight="600">Created At:</Text>
                                        <Text>{moment(selected.created_at).format("DD MMM YYYY")}</Text>
                                    </GridItem>

                                    <GridItem>
                                        <Text fontWeight="600">Push/In-App Cost:</Text>
                                        <Text>{selected.push_inapp_cost_per_msg}</Text>
                                    </GridItem>
                                    <GridItem>
                                        <Text fontWeight="600">WhatsApp Cost:</Text>
                                        <Text>{selected.whatsapp_cost_per_msg}</Text>
                                    </GridItem>

                                    <GridItem>
                                        <Text fontWeight="600">SMS Cost:</Text>
                                        <Text>{selected.sms_cost_per_msg}</Text>
                                    </GridItem>
                                    <GridItem>
                                        <Text fontWeight="600">Voice Cost:</Text>
                                        <Text>{selected.voice_cost_per_msg}</Text>
                                    </GridItem>
                                </Grid>

                                <Divider my={4} />

                                <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                                    <GridItem>
                                        <Text fontWeight="600">Name:</Text>
                                        <Text>
                                            {selected.user
                                                ? `${selected.user.first_name} ${selected.user.last_name}`
                                                : "-"}
                                        </Text>
                                    </GridItem>
                                    <GridItem>
                                        <Text fontWeight="600">Email:</Text>
                                        <Text>{selected.user?.business_email || "-"}</Text>
                                    </GridItem>
                                    <GridItem>
                                        <Text fontWeight="600">Mobile:</Text>
                                        <Text>{selected.user?.mobile_number || "-"}</Text>
                                    </GridItem>
                                </Grid>
                            </Box>
                        ) : (
                            <Text>No data available</Text>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="blue" onClick={closeModal}>
                            Close
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

export default EstimatesList;
